import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useMe } from '../../auth/useMe';
import { useSidebar } from './AppShell';

// Keep all inline navigation icons on the same stroke/size contract.
const ICON_PROPS = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true as const,
};

// Local SVGs avoid adding an icon dependency for this small, fixed nav surface.
const icons: Record<string, ReactNode> = {
  Dashboard: <svg {...ICON_PROPS}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>,
  Triage: <svg {...ICON_PROPS}><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>,
  Merge: <svg {...ICON_PROPS}><circle cx="18" cy="18" r="3" /><circle cx="6" cy="6" r="3" /><path d="M6 21V9a9 9 0 0 0 9 9" /></svg>,
  Lifecycle: <svg {...ICON_PROPS}><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>,
  Deals: <svg {...ICON_PROPS}><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /><path d="M12 2L2 7l10 5 10-5L12 2z" /></svg>,
  Overview: <svg {...ICON_PROPS}><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="12" x2="16" y2="12" /></svg>,
  Pipeline: <svg {...ICON_PROPS}><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></svg>,
  Entities: <svg {...ICON_PROPS}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
  Suppliers: <svg {...ICON_PROPS}><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>,
  Investors: <svg {...ICON_PROPS}><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
  Functions: <svg {...ICON_PROPS}><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></svg>,
  Admin: <svg {...ICON_PROPS}><path d="M12 3l8 4v5c0 5-3.4 8.4-8 10-4.6-1.6-8-5-8-10V7l8-4z" /><path d="M9 12l2 2 4-4" /></svg>,
  Back: <svg {...ICON_PROPS}><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>,
  Sources: <svg {...ICON_PROPS}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22 6 12 13 2 6" /></svg>,
  Exclusions: <svg {...ICON_PROPS}><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>,
  Skipped: <svg {...ICON_PROPS}><polygon points="5 4 15 12 5 20 5 4" /><line x1="19" y1="5" x2="19" y2="19" /></svg>,
  Rebucket: <svg {...ICON_PROPS}><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>,
  'Audit log': <svg {...ICON_PROPS}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>,
};

interface NavItem {
  to: string;
  label: string;
  children?: NavItem[];
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const mainGroups: NavGroup[] = [
  {
    label: 'Overview',
    items: [{ to: '/', label: 'Dashboard' }],
  },
  {
    label: 'Queues',
    items: [
      { to: '/triage', label: 'Triage' },
      { to: '/merge', label: 'Merge' },
      { to: '/lifecycle', label: 'Lifecycle' },
    ],
  },
  {
    label: 'Registers',
    items: [
      {
        to: '/registers/deals',
        label: 'Deals',
        children: [
          { to: '/registers/deals', label: 'Overview' },
          { to: '/registers/pipeline', label: 'Pipeline' },
        ],
      },
      { to: '/registers/entities', label: 'Entities' },
      { to: '/registers/suppliers', label: 'Suppliers' },
      { to: '/registers/investors', label: 'Investors' },
      { to: '/registers/functions', label: 'Functions' },
    ],
  },
];

// Admin lives as a second-level sidebar view so rare tools do not crowd daily navigation.
const adminItems: NavItem[] = [
  { to: '/admin/sources', label: 'Sources' },
  { to: '/admin/exclusions', label: 'Exclusions' },
  { to: '/admin/skipped', label: 'Skipped' },
  { to: '/admin/rebucket', label: 'Rebucket' },
  { to: '/audit', label: 'Audit log' },
];

// Admin routes should keep the sidebar in the admin view after navigation or refresh.
function isAdminPath(pathname: string) {
  return pathname.startsWith('/admin') || pathname === '/audit';
}

function SidebarNavLink({ item, collapsed, indent }: { item: NavItem; collapsed: boolean; indent?: boolean }) {
  return (
    <NavLink
      end={item.to === '/' || !!indent}
      to={item.to}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        clsx(
          'sidebar-nav-item relative flex min-h-11 items-center overflow-hidden rounded-md py-2 text-[0.8125rem] cursor-pointer motion-reduce:transition-none',
          'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-teal',
          collapsed ? 'justify-center px-0' : indent ? 'gap-2.5 pl-9 pr-2.5' : 'gap-2.5 px-2.5',
          isActive
            ? 'text-white font-semibold bg-white/[0.10]'
            : 'text-[#94a3b8] hover:text-white hover:bg-white/[0.05]',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-teal"
              aria-hidden="true"
            />
          )}
          <span
            className={clsx(
              'shrink-0 transition-opacity duration-150 motion-reduce:transition-none',
              isActive ? 'text-teal opacity-100' : 'opacity-65',
            )}
          >
            {icons[item.label]}
          </span>
          <span className="sidebar-text min-w-0 flex-1 truncate">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

function NavParent({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const location = useLocation();
  const isChildActive = item.children!.some((c) => location.pathname.startsWith(c.to));
  const [expanded, setExpanded] = useState(isChildActive);

  useEffect(() => {
    if (isChildActive) setExpanded(true);
  }, [isChildActive]);

  return (
    <li>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        title={collapsed ? item.label : undefined}
        className={clsx(
          'sidebar-nav-item relative flex min-h-11 w-full items-center overflow-hidden rounded-md py-2 text-[0.8125rem] cursor-pointer motion-reduce:transition-none',
          'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-teal',
          collapsed ? 'justify-center px-0' : 'gap-2.5 px-2.5',
          isChildActive
            ? 'text-white font-semibold bg-white/[0.10]'
            : 'text-[#94a3b8] hover:text-white hover:bg-white/[0.05]',
        )}
        aria-expanded={expanded}
      >
        {isChildActive && (
          <span
            className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-teal"
            aria-hidden="true"
          />
        )}
        <span
          className={clsx(
            'shrink-0 transition-opacity duration-150 motion-reduce:transition-none',
            isChildActive ? 'text-teal opacity-100' : 'opacity-65',
          )}
        >
          {icons[item.label]}
        </span>
        <span className="sidebar-text min-w-0 flex-1 text-left truncate">{item.label}</span>
        <span className="sidebar-text shrink-0 text-white/35" aria-hidden="true">
          <svg {...ICON_PROPS} width="14" height="14"
            style={{ transform: expanded ? 'rotate(90deg)' : undefined, transition: 'transform 200ms ease' }}
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </span>
      </button>
      {expanded && !collapsed && (
        <ul className="mt-0.5 space-y-0.5">
          {item.children!.map((child) => (
            <li key={child.to}>
              <SidebarNavLink item={child} collapsed={collapsed} indent />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function Sidebar() {
  const me = useMe();
  const isAdmin = me.data?.isAdmin ?? false;
  const { open, close, collapsed, toggleCollapsed } = useSidebar();
  const location = useLocation();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [adminMenuOpen, setAdminMenuOpen] = useState(() => isAdminPath(location.pathname));
  const [menuDirection, setMenuDirection] = useState<'forward' | 'back'>('forward');
  const accountName = me.data?.upn?.trim() || 'Unknown user';
  const accountInitial = accountName.charAt(0).toUpperCase() || '?';

  // Swap the whole menu group to keep keyboard order and visual order aligned.
  const visibleGroups = useMemo(
    () => (adminMenuOpen ? [{ label: 'Admin', items: adminItems }] : mainGroups),
    [adminMenuOpen],
  );

  useEffect(() => {
    if (!window.matchMedia('(min-width: 1024px)').matches) {
      close();
    }
    const nextIsAdminPath = isAdminPath(location.pathname);
    setMenuDirection(nextIsAdminPath ? 'forward' : 'back');
    setAdminMenuOpen(nextIsAdminPath);
  }, [location.pathname, close]);

  // Mobile drawer close restores focus to the control that opened it.
  const handleClose = useCallback(() => {
    close();
    requestAnimationFrame(() => {
      if (previousFocusRef.current && document.contains(previousFocusRef.current)) {
        previousFocusRef.current.focus();
      }
    });
  }, [close]);

  // While open on mobile, lock page scroll and support Escape like a modal drawer.
  useEffect(() => {
    if (!open) return;
    if (window.matchMedia('(min-width: 1024px)').matches) return;

    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    requestAnimationFrame(() => closeButtonRef.current?.focus());

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [handleClose, open]);

  return (
    <>
      <div
        className="sidebar-backdrop cursor-pointer"
        data-open={open}
        onClick={handleClose}
        aria-hidden="true"
      />

      <aside className="sidebar-panel" data-open={open} data-collapsed={collapsed} aria-label="Main navigation">
        <div className="h-[3px] shrink-0 bg-teal" />

        <div
          className="sidebar-header flex items-center justify-between overflow-hidden px-4 py-3"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div className="sidebar-text flex items-center gap-2.5">
            <img
              src="/images/di-logo.svg"
              alt="Digital Infrastructure"
              style={{ height: 28, filter: 'brightness(0) invert(1)' }}
            />
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={handleClose}
            className="lg:hidden flex items-center justify-center text-white/60 hover:text-white hover:bg-white/[0.06] rounded transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal"
            style={{ width: 44, height: 44 }}
            aria-label="Close navigation"
          >
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <button
          type="button"
          onClick={toggleCollapsed}
          className="sidebar-collapse-btn"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: collapsed ? 'rotate(180deg)' : undefined, transition: 'transform 200ms ease' }}
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        <nav
          className="scrollbar-thin flex-1 overflow-x-hidden overflow-y-auto px-3 py-3"
          aria-label={adminMenuOpen ? 'Admin navigation' : 'Main navigation'}
        >
          {/* Keyed wrapper remounts the menu so direction-aware transitions replay cleanly. */}
          <div
            id="admin-sidebar-menu"
            key={adminMenuOpen ? 'admin' : 'main'}
            className={clsx(
              'space-y-5 will-change-transform motion-reduce:animate-none',
              menuDirection === 'forward'
                ? 'motion-safe:animate-[sidebar-menu-forward_220ms_cubic-bezier(0.22,1,0.36,1)]'
                : 'motion-safe:animate-[sidebar-menu-back_220ms_cubic-bezier(0.22,1,0.36,1)]',
            )}
          >
            {adminMenuOpen && (
              <button
                type="button"
                onClick={() => {
                  setMenuDirection('back');
                  setAdminMenuOpen(false);
                }}
                className="sidebar-nav-item group mb-1 flex min-h-11 w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-[0.8125rem] font-semibold text-white/80 hover:bg-white/[0.05] hover:text-white cursor-pointer motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-teal"
              >
                <span className="shrink-0 text-teal transition-transform duration-200 motion-reduce:transition-none group-hover:-translate-x-0.5">
                  {icons.Back}
                </span>
                <span className="sidebar-text min-w-0 flex-1 text-left truncate">Back to menu</span>
              </button>
            )}

            {visibleGroups.map((group) => (
              <div key={group.label}>
                <div className="sidebar-group-label px-2.5 pb-2 text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-white/30">
                  {group.label}
                </div>
                <ul className="space-y-0.5">
                  {group.items.map((item) =>
                    item.children ? (
                      <NavParent key={item.to} item={item} collapsed={collapsed} />
                    ) : (
                      <li key={item.to}>
                        <SidebarNavLink item={item} collapsed={collapsed} />
                      </li>
                    ),
                  )}
                </ul>
              </div>
            ))}

            {isAdmin && !adminMenuOpen && (
              <div>
                <div className="sidebar-group-label px-2.5 pb-2 text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-white/30">
                  Access
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setMenuDirection('forward');
                    setAdminMenuOpen(true);
                  }}
                  className="sidebar-nav-item group relative flex min-h-11 w-full items-center gap-2.5 overflow-hidden rounded-md px-2.5 py-2 font-semibold text-[0.8125rem] text-[#94a3b8] hover:bg-white/[0.05] hover:text-white cursor-pointer motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-teal"
                  aria-expanded={adminMenuOpen}
                  aria-controls="admin-sidebar-menu"
                >
                  <span className="shrink-0 opacity-65">{icons.Admin}</span>
                  <span className="sidebar-text min-w-0 flex-1 text-left truncate">Admin</span>
                  <span className="sidebar-text shrink-0 text-white/35 group-hover:translate-x-0.5" aria-hidden="true">
                    <svg {...ICON_PROPS} width="16" height="16"><path d="M9 18l6-6-6-6" /></svg>
                  </span>
                </button>
              </div>
            )}
          </div>
        </nav>

        <div
          className="px-3 py-3"
          style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div className="sidebar-account flex items-center gap-2.5 overflow-hidden rounded-md bg-white/[0.04] px-2.5 py-1.5">
            <div
              className="flex items-center justify-center shrink-0 rounded-full bg-teal/20 text-teal text-[0.6875rem] font-bold uppercase"
              style={{ width: 30, height: 30 }}
              aria-hidden="true"
            >
              {accountInitial}
            </div>
            <div className="sidebar-text min-w-0 flex-1">
              <div className="text-white text-[0.8125rem] font-medium truncate">
                {accountName}
              </div>
            </div>
          </div>
        </div>

        <div className="h-[3px] shrink-0 bg-teal" />
      </aside>
    </>
  );
}
